import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, NetworkError, api, type PatientDetail, type PatientSummary } from '../api';
import type { PatientSource } from '../api-modules';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner } from '../components';
import { IconPlus, IconSearch } from '../icons';
import { PatientRecord } from './PatientRecord';

/**
 * The patient list, and the record it opens.
 *
 * Opening a patient replaces the list rather than floating a dialog over it.
 * A patient record is a place you work — charting, prescribing, billing — not
 * something you glance at and dismiss, and a modal cannot hold a dental chart
 * without becoming a window inside a window.
 */
export function Patients({
  clinicId,
  canWrite,
  permissions,
  specialties = ['dental'],
  features = [],
}: {
  clinicId: string;
  canWrite: boolean;
  permissions: string[];
  specialties?: string[];
  features?: string[];
}) {
  const { t, tx, locale, formatDate, formatNumber, personName, ageFrom } = useI18n();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState<PatientSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(id);
  }, [query]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.patients({ q: debounced || undefined, limit: 50 });
      setRows(res.patients);
      setTotal(res.total);
      setScope(res.scope);
    } catch (e) {
      setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error);
    }
  }, [debounced, t]);

  useEffect(() => {
    // Clinic change clears the list before refetching, so results from the
    // previous clinic are never on screen (spec §4).
    setRows(null);
    void load();
  }, [clinicId, load, nonce]);

  if (openId) {
    return <PatientRecord patientId={openId} permissions={permissions} specialties={specialties} features={features} onBack={() => setOpenId(null)} />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.patients.title}</h1>
          <div className="page-sub">
            {tx(t.patients.count, { count: formatNumber(total) })}
            {scope === 'assigned' ? ` · ${t.dashboard.assignedOnly}` : ''}
          </div>
        </div>
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            <IconPlus size={16} />
            {t.patients.add}
          </button>
        ) : null}
      </div>

      <div className="toolbar">
        <div className="search-box">
          <IconSearch size={16} className="icon" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.patients.search}
            aria-label={t.patients.search}
          />
        </div>
      </div>

      <div className="card">
        {error ? (
          <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : rows === null ? (
          <Skeleton rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={debounced ? t.patients.none : t.patients.noneYet}
            action={
              canWrite && !debounced ? (
                <button type="button" className="btn" onClick={() => setCreating(true)}>
                  + {t.patients.add}
                </button>
              ) : null
            }
          />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.patients.number}</th>
                  <th>{t.patients.name}</th>
                  <th>{t.patients.born}</th>
                  <th>{t.patients.phone}</th>
                  <th>
                    <span className="sr-only">{t.patients.actions}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const age = ageFrom(p.birthDate);
                  return (
                    <tr key={p.id} className="clickable" onClick={() => setOpenId(p.id)}>
                      <td className="ltr-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {p.patientNumber}
                      </td>
                      <td>
                        <strong>{personName(p)}</strong>
                        {/* Show the other script too, so a bilingual clinic can
                            match either spelling at a glance. */}
                        {p.givenNameAlt && p.familyNameAlt ? (
                          <span style={{ color: 'var(--ink-3)', display: 'block', fontSize: 12.5 }}>
                            {locale === 'ar'
                              ? `${p.givenName} ${p.familyName}`
                              : `${p.givenNameAlt} ${p.familyNameAlt}`}
                          </span>
                        ) : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {p.birthDate ? (
                          <>
                            {formatDate(p.birthDate)}
                            {age !== null ? (
                              <span style={{ color: 'var(--ink-3)' }}> · {tx(t.common.years, { n: age })}</span>
                            ) : null}
                          </>
                        ) : (
                          <span style={{ color: 'var(--ink-3)' }}>{t.common.notRecorded}</span>
                        )}
                      </td>
                      <td className="ltr-value">{p.phone ?? '—'}</td>
                      <td style={{ textAlign: 'end' }}>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenId(p.id);
                          }}
                        >
                          {t.patients.view}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <NewPatientModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setNonce((n) => n + 1);
            setOpenId(id);
          }}
        />
      ) : null}

    </>
  );
}

function NewPatientModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  return <PatientForm onClose={onClose} onSaved={onCreated} />;
}

/**
 * The patient's details, new or existing: one form for both, so a field added
 * here (like where the patient came from) is in registration and in editing
 * at once. Where they came from is one choice: one of the clinic's sources
 * (Settings, Patient sources) or a dentist of the clinic who referred them.
 */
export function PatientForm({
  patient,
  onClose,
  onSaved,
}: {
  /** Absent: a new patient. */
  patient?: PatientDetail;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t, locale } = useI18n();
  const v = (x: string | null | undefined) => x ?? '';
  const [form, setForm] = useState({
    givenName: v(patient?.givenName),
    familyName: v(patient?.familyName),
    givenNameAlt: v(patient?.givenNameAlt),
    familyNameAlt: v(patient?.familyNameAlt),
    birthDate: v(patient?.birthDate),
    sex: patient?.sex ?? 'unspecified',
    phone: v(patient?.phone),
    whatsappPhone: v(patient?.whatsappPhone),
    email: v(patient?.email),
    guardianName: v(patient?.guardianName),
    guardianRelation: v(patient?.guardianRelation),
    guardianPhone: v(patient?.guardianPhone),
    notes: v(patient?.notes),
    sourceDetail: v(patient?.sourceDetail),
  });
  // One select for both kinds of origin: "s:<source id>" or "d:<dentist membership id>".
  const [origin, setOrigin] = useState(
    patient?.sourceId ? `s:${patient.sourceId}` : patient?.referredByMembershipId ? `d:${patient.referredByMembershipId}` : '',
  );
  const [sources, setSources] = useState<PatientSource[]>([]);
  const [dentists, setDentists] = useState<{ membershipId: string; name: string }[]>([]);
  useEffect(() => {
    api.patientSources().then((r) => setSources(r.sources)).catch(() => undefined);
    api.appointmentResources().then((r) => setDentists(r.dentists)).catch(() => undefined);
  }, []);
  const sourceName = (x: PatientSource) => (locale === 'ar' && x.nameAr ? x.nameAr : x.nameEn);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setIssues({});
    try {
      const body = {
        ...form,
        sourceId: origin.startsWith('s:') ? origin.slice(2) : null,
        referredByMembershipId: origin.startsWith('d:') ? origin.slice(2) : null,
      };
      const res = patient ? await api.updatePatient(patient.id, body) : await api.createPatient(body);
      onSaved(res.patient.id);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // Field-level errors are shown beside their field; the form keeps
        // everything the user typed (spec §2: preserve work after errors).
        if (err.issues) {
          setIssues(Object.fromEntries(err.issues.map((i) => [i.path, i.message])));
        }
      } else if (err instanceof NetworkError) {
        setError(t.errors.network);
      } else {
        setError(t.common.error);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={patient ? t.patientEdit.title : t.patients.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="submit" form="new-patient" className="btn" disabled={busy || !form.givenName || !form.familyName}>
            {busy ? (
              <>
                <Spinner /> {t.patients.saving}
              </>
            ) : (
              t.patients.save
            )}
          </button>
        </>
      }
    >
      <form id="new-patient" onSubmit={submit} className="stack" noValidate>
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="grid-2">
          <Field label={t.patients.givenName} required error={issues.givenName}>
            <input value={form.givenName} onChange={set('givenName')} required autoFocus />
          </Field>
          <Field label={t.patients.familyName} required error={issues.familyName}>
            <input value={form.familyName} onChange={set('familyName')} required />
          </Field>
          <Field label={t.patients.givenNameAlt} hint={t.patients.optional}>
            <input value={form.givenNameAlt} onChange={set('givenNameAlt')} dir="rtl" />
          </Field>
          <Field label={t.patients.familyNameAlt} hint={t.patients.optional}>
            <input value={form.familyNameAlt} onChange={set('familyNameAlt')} dir="rtl" />
          </Field>
          <Field label={t.patients.birthDate} hint={t.patients.optional} error={issues.birthDate}>
            <input type="date" value={form.birthDate} onChange={set('birthDate')} className="ltr-value" />
          </Field>
          <Field label={t.patients.sex}>
            <select value={form.sex} onChange={set('sex')}>
              <option value="unspecified">{t.sex.unspecified}</option>
              <option value="female">{t.sex.female}</option>
              <option value="male">{t.sex.male}</option>
              <option value="other">{t.sex.other}</option>
            </select>
          </Field>
        </div>

        <div className="section-label">{t.patients.phone}</div>
        <p className="hint" style={{ color: 'var(--ink-3)', margin: 0, fontSize: 12 }}>
          {t.patients.sharedContactHint}
        </p>
        <div className="grid-3">
          <Field label={t.patients.phone} hint={t.patients.optional}>
            <input value={form.phone} onChange={set('phone')} className="ltr-value" inputMode="tel" />
          </Field>
          <Field label={t.patients.whatsapp} hint={t.patients.optional}>
            <input value={form.whatsappPhone} onChange={set('whatsappPhone')} className="ltr-value" inputMode="tel" />
          </Field>
          <Field label={t.patients.emailAddress} hint={t.patients.optional} error={issues.email}>
            <input type="email" value={form.email} onChange={set('email')} className="ltr-value" />
          </Field>
        </div>

        <div className="section-label">{t.sources.label}</div>
        <div className="grid-2">
          <Field label={t.sources.label} hint={t.patients.optional} error={issues.sourceId ?? issues.referredByMembershipId}>
            <select value={origin} onChange={(e) => setOrigin(e.target.value)}>
              <option value="">{t.sources.none}</option>
              {sources.map((x) => (
                <option key={x.id} value={`s:${x.id}`}>
                  {sourceName(x)}
                </option>
              ))}
              {/* A retired source this patient already has stays selectable for them. */}
              {patient?.sourceId && !sources.some((x) => x.id === patient.sourceId) && patient.sourceName ? (
                <option value={`s:${patient.sourceId}`}>{locale === 'ar' && patient.sourceName.ar ? patient.sourceName.ar : patient.sourceName.en}</option>
              ) : null}
              {dentists.length > 0 ? (
                <optgroup label={t.sources.dentistsGroup}>
                  {dentists.map((d) => (
                    <option key={d.membershipId} value={`d:${d.membershipId}`}>
                      {t.sources.referredByOption.replace('{name}', d.name)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </Field>
          <Field label={t.sources.detail} hint={t.sources.detailHint}>
            <input value={form.sourceDetail} onChange={set('sourceDetail')} maxLength={200} />
          </Field>
        </div>

        <div className="section-label">{t.patients.guardian}</div>
        <div className="grid-3">
          <Field label={t.patients.guardianName} hint={t.patients.optional}>
            <input value={form.guardianName} onChange={set('guardianName')} />
          </Field>
          <Field label={t.patients.guardianRelation} hint={t.patients.optional}>
            <input value={form.guardianRelation} onChange={set('guardianRelation')} />
          </Field>
          <Field label={t.patients.guardianPhone} hint={t.patients.optional}>
            <input value={form.guardianPhone} onChange={set('guardianPhone')} className="ltr-value" inputMode="tel" />
          </Field>
        </div>

        <Field label={t.patients.notes} hint={t.patients.optional}>
          <textarea value={form.notes} onChange={set('notes')} rows={3} />
        </Field>
      </form>
    </Modal>
  );
}
